import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import {
  Calendar as CalIcon, MapPin, Users, Baby, Search, Plane, Hotel, Ticket, Car, Mountain,
  Minus, Plus, Globe, Home, Compass, Ship, Train, Bus, Bike, Camera, TreePine, Umbrella,
  Palmtree, Tent, Landmark, Building, Castle, Waves, Sun, Sunset, Anchor, Sparkles,
  Luggage, Map, Navigation, Coffee, Utensils, Wine, Music, Star, Heart, ShoppingBag,
  Gift, Rocket, Gem, Crown, Trophy, Zap, Flame, Eye, BookOpen, Feather, Leaf, Flower2,
} from "lucide-react";

// Map icon names from admin/categories to lucide components
const ICON_MAP = {
  Plane, Hotel, Ticket, Car, Mountain, Globe, Home, Compass, Ship, Train, Bus, Bike, Camera,
  TreePine, Umbrella, MapPin, Users, Search, Palmtree, Tent, Landmark, Building, Castle,
  Waves, Sun, Sunset, Anchor, Sparkles, Luggage, Map, Navigation, Coffee, Utensils, Wine,
  Music, Star, Heart, ShoppingBag, Gift, Rocket, Gem, Crown, Trophy, Zap, Flame, Eye,
  BookOpen, Feather, Leaf, Flower2,
};

// Fallback tabs if categories can't be loaded
const FALLBACK_TABS = [
  { id: "tour", label: "Tours", icon: "Bus", slug: "tour" },
  { id: "hotel", label: "Hotels", icon: "Hotel", slug: "hotel" },
  { id: "ticket", label: "Tickets", icon: "Ticket", slug: "ticket" },
  { id: "cab", label: "Cabs", icon: "Car", slug: "cab" },
  { id: "activity", label: "Adventure", icon: "Mountain", slug: "activity" },
];

function PaxStepper({ icon: Icon, label, sub, value, onChange, min = 0, testid }) {
  return (
    <div className="bg-white border border-border rounded-xl px-3 py-2 flex items-center gap-2" data-testid={testid}>
      <Icon className="w-4 h-4 text-teal shrink-0" />
      <div className="flex-1 min-w-0">
        {sub && <div className="text-[11px] text-muted-foreground leading-none">{sub}</div>}
        <div className="text-sm font-semibold leading-tight">{label}</div>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="w-6 h-6 rounded-full border border-border flex items-center justify-center hover:border-teal disabled:opacity-30"
          data-testid={`${testid}-minus`}
        >
          <Minus className="w-3 h-3" />
        </button>
        <span className="w-5 text-center text-sm font-semibold" data-testid={`${testid}-value`}>{value}</span>
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          className="w-6 h-6 rounded-full border border-border flex items-center justify-center hover:border-teal"
          data-testid={`${testid}-plus`}
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

export default function SearchTabs() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [tabs, setTabs] = useState(FALLBACK_TABS);
  const [tab, setTab] = useState("tour");
  const [destination, setDestination] = useState("");
  const [date, setDate] = useState();
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);

  // Load categories from API for tabs
  useEffect(() => {
    api.get("/categories").then((r) => {
      const cats = r.data.filter((c) => !c.parent_id); // only root categories
      if (cats.length > 0) {
        // Deduplicate by product_category to avoid duplicate tabs
        const seen = new Set();
        const mapped = cats.filter((c) => {
          const pc = c.product_category || c.slug;
          if (seen.has(pc)) return false;
          seen.add(pc);
          return true;
        }).slice(0, 6).map((c) => ({
          id: c.product_category || c.slug,
          label: c.name.replace(" & Resorts", "").replace(" & Transport", ""),
          icon: c.icon || "MapPin",
          slug: c.product_category || c.slug,
        }));
        setTabs(mapped);
        if (mapped.length > 0) setTab(mapped[0].id);
      }
    }).catch(() => {});
  }, []);

  // Age labels from admin settings
  const adultMinAge = settings.adult_min_age || 12;
  const childMinAge = settings.child_min_age || 4;
  const childMaxAge = settings.child_max_age || 11;
  const adultAgeLabel = `${adultMinAge}+ yrs`;
  const childAgeLabel = `${childMinAge}-${childMaxAge} yrs`;

  const onSearch = () => {
    const params = new URLSearchParams({ category: tab });
    if (destination) params.set("destination", destination);
    if (date) params.set("date", format(date, "yyyy-MM-dd"));
    navigate(`/search?${params.toString()}`);
  };

  const placeholders = {
    "sic-tours": "Where to? (Kashmir, Goa, Warangal...)",
    "tour": "Where to? (Kashmir, Goa, Warangal...)",
    "hotels": "Destination or hotel name",
    "hotel": "Destination or hotel name",
    "staycations": "Resort or location name",
    "entry-tickets": "Search venue (Ramoji, Wonderla...)",
    "ticket": "Search venue (Ramoji, Wonderla...)",
    "cabs": "City or route",
    "cab": "City or route",
    "adventure": "Activity or place",
    "activity": "Activity or place",
    "international": "Country or destination",
  };

  // Animated placeholder ticker - use admin settings or Hyderabad-focused defaults
  const defaultSuggestions = [
    "Hyderabad City Tour",
    "Ramoji Film City Tickets",
    "Charminar Heritage Walk",
    "Srisailam Temple Tour",
    "Nagarjuna Sagar Weekend",
    "Ananthagiri Hills Trek",
    "Wonderla Water Park",
    "Hussain Sagar Boating",
    "Golconda Fort Light Show",
    "Warangal Day Trip",
  ];
  const tickerSuggestions = (settings.search_ticker_suggestions && settings.search_ticker_suggestions.length > 0)
    ? settings.search_ticker_suggestions
    : defaultSuggestions;
  const [tickerPlaceholder, setTickerPlaceholder] = useState("");

  useEffect(() => {
    if (destination) return;
    let cancelled = false;
    let idx = 0;

    const animateSuggestion = async () => {
      while (!cancelled) {
        const text = tickerSuggestions[idx % tickerSuggestions.length];
        // Type forward
        for (let i = 1; i <= text.length; i++) {
          if (cancelled) return;
          setTickerPlaceholder(text.slice(0, i));
          await new Promise((r) => setTimeout(r, 60));
        }
        // Pause at full text
        await new Promise((r) => setTimeout(r, 1800));
        // Erase backward
        for (let i = text.length - 1; i >= 0; i--) {
          if (cancelled) return;
          setTickerPlaceholder(text.slice(0, i));
          await new Promise((r) => setTimeout(r, 30));
        }
        await new Promise((r) => setTimeout(r, 300));
        idx++;
      }
    };

    animateSuggestion();
    return () => { cancelled = true; };
  }, [destination]);

  return (
    <div className="glass border border-white/40 rounded-2xl shadow-2xl p-2 sm:p-3" data-testid="home-search-tabs">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent justify-start gap-1 h-auto flex-wrap w-full" data-testid="search-tabs-list">
          {tabs.map((t) => {
            const Icon = ICON_MAP[t.icon] || MapPin;
            return (
              <TabsTrigger
                key={t.id}
                value={t.id}
                className="data-[state=active]:bg-teal data-[state=active]:text-white rounded-lg px-4 py-2 flex items-center gap-2"
                data-testid={`tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{t.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value={tab} className="mt-3 sm:mt-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 sm:gap-3">
            <div className="md:col-span-6 flex items-center gap-2 bg-white border border-border rounded-xl px-3 py-2.5">
              <MapPin className="w-4 h-4 text-teal shrink-0" />
              <Input
                placeholder={destination ? "" : (tickerPlaceholder || placeholders[tab] || "Search destination...")}
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                className="border-0 focus-visible:ring-0 px-0 h-9"
                data-testid="search-destination-input"
              />
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="md:col-span-3 flex items-center gap-2 bg-white border border-border rounded-xl px-3 py-2.5 text-sm text-left hover:border-teal transition-colors"
                  data-testid="search-date-trigger"
                >
                  <CalIcon className="w-4 h-4 text-teal shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[11px] text-muted-foreground leading-none">Travel</div>
                    <div className="text-sm font-semibold leading-tight truncate">
                      {date ? format(date, "d MMM") : "Pick date"}
                    </div>
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="p-0 w-auto">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
              </PopoverContent>
            </Popover>

            <Button
              onClick={onSearch}
              className="md:col-span-3 bg-saffron hover:bg-saffron-dark text-white h-12 rounded-xl text-base font-medium"
              data-testid="home-search-button"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
